import React from 'react';
import { AjaxLeadFormModel, GtmAnalyticsData } from '../../models/globals';
import { publishAnalyticsEvent } from '../../services/analytics';
import { xhrClient } from '../../services/xhrClient';
import { FormName, LeadForm } from './LeadForm';

const navigateIfDefined = (url: string | null): void => {
    if (url) {
        window.location.assign(url);
    }
};

interface AjaxLeadFormProps extends AjaxLeadFormModel {
    formName: FormName;
    onLeadCreationError?(): void;
    onLeadCreationSuccess?(): void;
}

const AjaxLeadForm: React.FC<AjaxLeadFormProps> = ({
    ajaxLeadCreationApiPath,
    errorPath,
    formName,
    leadAttributes,
    leadCreationApiPath,
    onLeadCreationError,
    onLeadCreationSuccess,
    successPath,
    isRecaptchaEnabled,
    siteKey,
    baseUrl,
}) => {
    const [formData, setFormData] = React.useState<FormData>();

    React.useEffect(() => {
        if (formData) {
            // URLSearchParams(init: FormData) is fine since we're not submitting files, cast to `URLSearchParams` to satisfy TS
            const data = new URLSearchParams(formData as URLSearchParams).toString();
            const { cancel, isCancel, promise } = xhrClient.postLeadForm(ajaxLeadCreationApiPath, data);

            (async function getFormResponse() {
                try {
                    const response = await promise;
                    try {
                        const analyticsData: GtmAnalyticsData = {
                            containerId: leadAttributes.analyticsContainerId,
                            message: {
                                ...leadAttributes.analyticsData,
                                ...response.data.analyticsData,
                            },
                        };

                        await publishAnalyticsEvent(analyticsData);
                    } catch (e) {
                        console.error(e); // analytics failure shouldn't affect user experience
                    } finally {
                        onLeadCreationSuccess?.();
                        navigateIfDefined(successPath);
                    }
                } catch (thrown) {
                    if (!isCancel(thrown)) {
                        console.error(thrown);
                        onLeadCreationError?.();
                        navigateIfDefined(errorPath);
                    }
                }
            })();

            return function cleanup() {
                cancel(`Canceled lead form submission with data: "${data}"`);
            };
        }
    }, [formData]);

    return (
        <LeadForm
            formName={formName}
            leadAttributes={leadAttributes}
            leadCreationApiPath={leadCreationApiPath}
            onSubmit={(event) => {
                event.preventDefault();
                setFormData(new FormData(event.currentTarget));
            }}
            isRecaptchaEnabled={isRecaptchaEnabled}
            onLeadCreationError={onLeadCreationError}
            onLeadCreationSuccess={onLeadCreationSuccess}
            siteKey={siteKey}
            baseUrl={baseUrl}
        />
    );
};

export { AjaxLeadForm };
